"use client";

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSession } from 'next-auth/react';
import type { SubscriptionInfo } from '@/lib/stripe';
import { clientLogger } from '@/lib/client-logger';

interface UseSubscriptionReturn {
  subscription: SubscriptionInfo | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  canPerformAction: (action: 'create_campaign' | 'add_clue' | 'claim_asset', currentCount?: number) => Promise<{
    allowed: boolean;
    reason?: string;
    upgradeRequired?: string;
  }>;
}

export function useSubscription(): UseSubscriptionReturn {
  const { data: session } = useSession();
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscription = useCallback(async (forceRefresh = false) => {
    if (!session?.user) {
      setIsLoading(false);
      return;
    }

    try {
      setError(null);
      
      const url = '/api/subscription/status';
      const options: RequestInit = {
        method: forceRefresh ? 'POST' : 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      };

      if (forceRefresh) {
        options.body = JSON.stringify({ action: 'refresh' });
      }

      const response = await fetch(url, options);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch subscription');
      }

      setSubscription(data.subscription);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      clientLogger.error("Subscription fetch error", {
        error: err instanceof Error ? err.message : "Unknown error"
      });
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    await fetchSubscription(true);
  }, [fetchSubscription]);

  const canPerformAction = useCallback(async (
    action: 'create_campaign' | 'add_clue' | 'claim_asset',
    currentCount?: number
  ) => {
    if (!subscription) {
      return {
        allowed: false,
        reason: 'Subscription information not available',
      };
    }

    try {
      const response = await fetch('/api/subscription/check-limits', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action,
          currentCount,
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to check limits');
      }

      return data;
    } catch (err) {
      clientLogger.error("Limit check error", {
        error: err instanceof Error ? err.message : "Unknown error"
      });
      return {
        allowed: false,
        reason: 'Unable to verify limits',
      };
    }
  }, [subscription]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Post-purchase polling: when URL has success=true&plan=X, poll until
  // the subscription matches the expected plan, then clean up URL params.
  const pollingRef = useRef(false);
  useEffect(() => {
    if (!session?.user) return;

    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const expectedPlan = params.get('plan');

    if (success !== 'true' || !expectedPlan) return;
    if (pollingRef.current) return;
    pollingRef.current = true;

    clientLogger.info("Post-purchase polling started", { expectedPlan });

    let attempts = 0;
    const maxAttempts = 30; // up to 60 seconds
    const pollInterval = 2000;

    const poll = async () => {
      attempts++;
      try {
        const response = await fetch('/api/subscription/status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'refresh' }),
        });
        const data = await response.json();

        if (response.ok && data.subscription) {
          setSubscription(data.subscription);
          setIsLoading(false);

          if (data.subscription.plan === expectedPlan.toUpperCase()) {
            clientLogger.info("Subscription updated after purchase", {
              plan: data.subscription.plan,
              attempts,
            });
            // Clean success/plan params from URL without reload
            const url = new URL(window.location.href);
            url.searchParams.delete('success');
            url.searchParams.delete('plan');
            url.searchParams.delete('canceled');
            window.history.replaceState({}, '', url.toString());
            pollingRef.current = false;
            return; // Stop polling
          }
        }
      } catch (err) {
        clientLogger.error("Post-purchase poll error", {
          error: err instanceof Error ? err.message : "Unknown error",
        });
      }

      if (attempts < maxAttempts) {
        setTimeout(poll, pollInterval);
      } else {
        clientLogger.warn("Post-purchase polling timed out", { expectedPlan, attempts });
        pollingRef.current = false;
      }
    };

    // Start polling immediately with a force refresh
    poll();

    return () => {
      pollingRef.current = false;
    };
  }, [session]);  

  // Auto-refresh subscription status every 5 minutes
  useEffect(() => {
    if (!session?.user) return;

    const interval = setInterval(() => {
      fetchSubscription();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [session, fetchSubscription]);

  return {
    subscription,
    isLoading,
    error,
    refresh,
    canPerformAction,
  };
}
